import { forwardRef } from 'react';
import { Search, X } from 'lucide-react';
import Button from '../ui/Button';

/**
 * Shared glass search input — one layout for every surface (feed, search routes, triggers).
 * Icon is always visible; left padding is always reserved. No focus/idle style branches.
 */
const SearchField = forwardRef(function SearchField(
  {
    value = '',
    onChange,
    placeholder = 'Search',
    onClear,
    autoFocus = false,
    readOnly = false,
    onActivate,
    className = '',
    'aria-label': ariaLabel,
  },
  ref,
) {
  const isTrigger = readOnly && onActivate;
  const showClear = Boolean(onClear && value);

  const activate = () => {
    if (isTrigger) onActivate();
  };

  return (
    <div
      className={['search-field', isTrigger && 'search-field--trigger', className]
        .filter(Boolean)
        .join(' ')}
      role={isTrigger ? 'button' : undefined}
      tabIndex={isTrigger ? 0 : undefined}
      aria-label={isTrigger ? (ariaLabel ?? placeholder) : undefined}
      onClick={isTrigger ? activate : undefined}
      onKeyDown={
        isTrigger
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate();
              }
            }
          : undefined
      }
    >
      <input
        ref={ref}
        type="text"
        inputMode="search"
        enterKeyHint="search"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoFocus={autoFocus}
        readOnly={readOnly}
        tabIndex={isTrigger ? -1 : undefined}
        aria-label={!isTrigger ? ariaLabel : undefined}
        className="text-input search-field-input"
      />
      <Search className="search-field-icon" aria-hidden />
      {showClear ? (
        <Button
          type="button"
          aria-label="Clear search"
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="search-field-clear !h-auto !w-auto !min-h-0 !min-w-0 !p-1.5 rounded-md !border-0 !bg-transparent text-gray-400 hover:text-gray-600 dark:!text-dark-muted dark:hover:!text-dark-text"
        >
          <X className="w-5 h-5" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
});

export default SearchField;
